import { isPill } from './placementModel'
// Pure helpers for the OverlayEditor properties panel (box model).
//
// Fonts are limited to a fixed supported set (backend renderer only ships
// these families), each with its own valid weight range — JetBrains Mono
// tops out at 800 (no black), the others go to 900.

import { DEFAULT_TEXT_W } from './placementModel'

export const SUPPORTED_FONTS = ['Roboto', 'Roboto Condensed', 'Archivo', 'JetBrains Mono', 'Fira Sans Condensed', 'Play', 'MismatchSeg7']

// Seven-segment display faces carry no meaningful word glyphs, so they are offered
// on numeric fields only (the backend rejects them elsewhere on save).
export const DIGIT_ONLY_FONTS = new Set(['MismatchSeg7'])

// Applies a field's chosen display format to preview text, mirroring the backend's
// PeriodValueFormat (past regulation the prefixed/ordinal forms become overtime).
const ORDINALS = ['1st', '2nd', '3rd', '4th']
const REGULATION_PERIODS = 4

// Mirrors PlacementValueDecoration on the server: free text either side of the value,
// and neither side renders when the value is empty — so a field with no data never
// leaves an orphaned unit behind.
export function decorateValue(text, { valuePrefix, valueSuffix } = {}) {
  if (text === '' || text == null) return ''
  return `${valuePrefix ?? ''}${text}${valueSuffix ?? ''}`
}

// The canvas string for a placement: format first, then the designer's prefix/suffix.
export function displayValue(text, placement = {}) {
  return decorateValue(applyValueFormat(text, placement.valueFormat), placement)
}

export function applyValueFormat(text, valueFormat) {
  if (!valueFormat || valueFormat === 'plain') return text
  const period = Number(text)
  if (!Number.isInteger(period) || period < 1) return text
  if (period > REGULATION_PERIODS) return `OT${period - REGULATION_PERIODS}`
  return valueFormat === 'ordinal' ? ORDINALS[period - 1] : `Q${period}`
}

// Matches a board's placements against the id-named rects in its artwork. Fields the SVG
// names but the board has not placed yet are always appended — that is what saves an
// operator from hand-placing 54 slots on a fresh board.
//
// Geometry of a placement that ALREADY exists is only replaced when `overrideExisting` is
// set, i.e. when new artwork has just been handed over. On a plain load the stored values
// win: an operator's own resize has to survive reopening the editor.
const SVG_GEOMETRY_KEYS = ['x', 'y', 'w', 'h', 'imageWidth', 'imageHeight']

// A pill is positioned by an ANCHOR, not a box: x is the edge its text anchor names and
// its size lives in bgWidth/bgHeight. The rect in the SVG is the box (a bar's full track,
// a stripe's extent), so copying x/y/w/h onto it moves a right-anchored bar a whole
// track-width off. Convert the box into the anchor the renderer expects instead.
function pillFromSvgBox(stored, box) {
  if (box.w == null || box.h == null) return stored
  const a = stored.textAnchor
  const x = a === 'right' ? box.x + box.w : a === 'left' ? box.x : box.x + box.w / 2
  return {
    ...stored,
    x: Math.round(x * 1000) / 1000,
    y: Math.round((box.y + box.h / 2) * 1000) / 1000,
    bgWidth: box.w,
    bgHeight: box.h,
  }
}

export function mergeSvgGeometry(stored, fromSvg, { overrideExisting = false } = {}) {
  const pending = new Map(fromSvg.map((a) => [a.fieldId, a]))
  const merged = stored.map((p) => {
    const a = pending.get(p.fieldId)
    if (!a) return p
    pending.delete(p.fieldId)
    if (!overrideExisting) return p
    if (isPill(p)) return pillFromSvgBox(p, a)
    const next = { ...p }
    for (const k of SVG_GEOMETRY_KEYS) {
      if (a[k] !== undefined) next[k] = a[k]
    }
    return snapToCrop(next)
  })
  // Appended fields come straight from the rects, so they take the crop too. Placements
  // that were merely loaded keep exactly what was stored — snapping those would undo an
  // operator's own resize, which is the whole point of not overriding on load.
  return [...merged, ...[...pending.values()].map(snapToCrop)]
}

// The competition mark uploads at a fixed 16:9 crop, so its box takes that aspect whatever
// ratio the rect was drawn at — anything else only letterboxes at render time. Width is
// derived from the height, matching overlay-defaults-ds/_from_svg.py: the slot sits in a
// header band with no vertical room, so growing the height would overhang the card.
function snapToCrop(p) {
  if (p.fieldId !== 'competition_logo' || p.imageHeight == null) return p
  const width = +(p.imageHeight * (CANVAS_H / CANVAS_W) * COMPETITION_LOGO_ASPECT).toFixed(4)
  return width === p.imageWidth ? p : { ...p, imageWidth: width }
}

// Keeps an already-saved font in the list so the picker never renders blank.
export function fontOptionsFor(field, current) {
  const allowed = SUPPORTED_FONTS.filter((name) => !DIGIT_ONLY_FONTS.has(name) || field?.numeric)
  return current && !allowed.includes(current) ? [...allowed, current] : allowed
}

// Exact weights available per font — the picker offers only these, so changing
// the weight always renders. Variable fonts (Roboto / Roboto Condensed / Archivo
// / JetBrains Mono) are loaded across their full axis in public/index.html and
// the backend @font-face; the rest ship discrete static weights only.
export const FONT_WEIGHTS = {
  Roboto: [100, 200, 300, 400, 500, 600, 700, 800, 900],
  'Roboto Condensed': [100, 200, 300, 400, 500, 600, 700, 800, 900],
  Archivo: [100, 200, 300, 400, 500, 600, 700, 800, 900],
  'JetBrains Mono': [100, 200, 300, 400, 500, 600, 700, 800],
  'Fira Sans Condensed': [400, 500, 600, 700, 800, 900],
  Play: [400, 700],
  MismatchSeg7: [700],
}

export const WEIGHT_STEPS = [100, 200, 300, 400, 500, 600, 700, 800, 900]

const DEFAULT_FONT_SIZE = 32

// Line-height as % of 1080 from font size (px). Mirrors placementModel's
// single-line textH so a freshly-placed field starts at a sensible box
// height before the user resizes it.
const defaultTextHeight = (fontSize = DEFAULT_FONT_SIZE) =>
  Math.round((fontSize * 1.4 / 1080) * 100 * 1000) / 1000

// The weights actually available for the given font — only these are offered so
// a weight change always renders. Falls back to the full step list for an
// unrecognized font name rather than returning empty.
export function weightOptionsFor(font) {
  return FONT_WEIGHTS[font] ?? WEIGHT_STEPS
}

// A bar rect's anchor X for the given fill direction, in canvas-percent. Same three
// cases as pillFromSvgBox's text anchor: right → the rect's right edge, left → its
// left edge, center → its midpoint.
export function barAnchorX(hAlign, bbox, vw) {
  const x = hAlign === 'right' ? bbox.x + bbox.width
    : hAlign === 'center' ? bbox.x + bbox.width / 2
    : bbox.x
  return +(x / vw * 100).toFixed(2)
}

// Map an SVG element's bounding box (in element-local viewBox units) straight
// to a box-model placement in canvas-% units — no legacy point-anchored shape
// in between. Used by the SVG auto-place path (OverlayEditor's parseSvg) so a
// freshly auto-matched field is box-model from the start, same as a
// hand-dropped field via newFieldPlacement above.
export function svgRectToPlacement(field, bbox, viewBox) {
  const { w: vw, h: vh } = viewBox
  const x = +(bbox.x / vw * 100).toFixed(2)
  const y = +(bbox.y / vh * 100).toFixed(2)
  if (field.dataType === 'Image') {
    return {
      fieldId: field.key,
      x,
      y,
      imageWidth: +(bbox.width / vw * 100).toFixed(2),
      imageHeight: +(bbox.height / vh * 100).toFixed(2),
    }
  }
  if (field.dataType === 'Bar') {
    // A rect carries no anchor, so the fill direction comes from the side in the key:
    // home bars grow leftward out of the centre gutter, everything else grows rightward.
    const hAlign = field.key.startsWith('home_') ? 'right' : 'left'
    const anchorX = barAnchorX(hAlign, bbox, vw)
    const centerY = +((bbox.y + bbox.height / 2) / vh * 100).toFixed(2)
    return {
      fieldId: field.key,
      x: anchorX,
      y: centerY,
      w: +(bbox.width / vw * 100).toFixed(2),
      h: +(bbox.height / vh * 100).toFixed(2),
      hAlign,
    }
  }
  if (field.dataType === 'ColorBox') {
    // A color box fills its box, so there is no anchor to convert: the rect's top-left is the box.
    return {
      fieldId: field.key,
      x,
      y,
      w: +(bbox.width / vw * 100).toFixed(2),
      h: +(bbox.height / vh * 100).toFixed(2),
    }
  }
  return {
    fieldId: field.key,
    x,
    y,
    w: +(bbox.width / vw * 100).toFixed(2),
    h: +(bbox.height / vh * 100).toFixed(2),
    hAlign: 'center',
    vAlign: 'middle',
    fit: 'wrap',
  }
}

// The frame every placement percentage is measured against.
const CANVAS_W = 1920
const CANVAS_H = 1080

export const SPONSORS_FIELD_ID = 'sponsors'

// The crops the platform stores logo art at: sponsors are cropped 4:3 by
// EventFormCourtLogos, competition marks 16:9 by EventTeamsFields. Team crests and
// player photos are square.
export const SPONSOR_LOGO_ASPECT = 4 / 3
export const COMPETITION_LOGO_ASPECT = 16 / 9
export const SPONSOR_GAP_FRACTION = 0.08

// The area has to hold the fullest game, so the canvas lays out five whatever a
// particular game carries. The server preview draws the real count.
export const SPONSOR_PREVIEW_COUNT = 5

// Logos per row when the placement does not say — five fills one line.
export const SPONSOR_DEFAULT_PER_ROW = 5

// Every field whose art arrives at a fixed crop: competition_logo at 16:9
// (EventTeamsFields), team crests square (MyAddLogoInput uploads 400x400), player photos
// square (player-avatar.svg, and every photo box on the boards). The editor locks their
// aspect while resizing — changing such a box's ratio can only letterbox the art inside
// it. The sponsor AREA is excluded on purpose: its aspect is a design choice, not a crop.
export const locksAspectByDefault = (fieldId = '') =>
  fieldId === 'competition_logo' || /_photo$/.test(fieldId) || /(^|_)team_logo$/.test(fieldId)

export function imageAspectFor(fieldKey = '') {
  if (fieldKey === 'competition_logo') return COMPETITION_LOGO_ASPECT
  if (fieldKey === SPONSORS_FIELD_ID || /^sponsor_\d+$/.test(fieldKey)) return SPONSOR_LOGO_ASPECT
  return 1
}

// A pixel aspect expressed in the two axes' own percent units: H% is a share of 1080
// and W% a share of 1920, so a square box is 8% × 14.22%, not 8% × 8%.
export const imageHeightPctFor = (widthPct, aspect) =>
  +((widthPct * (CANVAS_W / CANVAS_H)) / aspect).toFixed(2)

// A placement colour may carry alpha as a fourth hex byte (the design's soft and dim inks
// are #17161A at 74% and 52%). A native <input type="color"> cannot express that, so the
// panel splits the value: the picker edits the RGB, a separate control edits the opacity.
const expandHex = (value = '') => {
  const m = /^#([0-9a-fA-F]{3,8})$/.exec(value.trim())
  if (!m) return null
  const d = m[1]
  if (d.length === 3 || d.length === 4) {
    const long = [...d].map((c) => c + c).join('')
    return { rgb: `#${long.slice(0, 6)}`, alpha: d.length === 4 ? parseInt(long.slice(6), 16) : null }
  }
  if (d.length === 6) return { rgb: `#${d}`, alpha: null }
  if (d.length === 8) return { rgb: `#${d.slice(0, 6)}`, alpha: parseInt(d.slice(6), 16) }
  return null
}

export const rgbOfColor = (value, fallback = '#ffffff') =>
  (expandHex(value)?.rgb ?? fallback).toLowerCase()

export const opacityPercentOfColor = (value) => {
  const parsed = expandHex(value)
  if (!parsed || parsed.alpha === null) return 100
  return Math.round((parsed.alpha / 255) * 100)
}

// Keeps whatever opacity the value already carried.
export const withRgb = (value, rgb) => {
  const alpha = expandHex(value)?.alpha
  return alpha === null || alpha === undefined
    ? rgb
    : `${rgb}${alpha.toString(16).padStart(2, '0')}`.toUpperCase()
}

// Fully opaque drops the byte entirely, so an ordinary colour stays six digits.
export const withOpacityPercent = (value, percent) => {
  const rgb = expandHex(value)?.rgb ?? '#ffffff'
  const clamped = Math.max(0, Math.min(100, Math.round(percent)))
  if (clamped >= 100) return rgb.toUpperCase()
  return `${rgb}${Math.round((clamped / 100) * 255).toString(16).padStart(2, '0')}`.toUpperCase()
}

// Row counts for `count` logos, mirroring SponsorAreaLayout.Rows: perRow per line, with a
// short final row for hAlign to position. The default of five (the most a game carries) is
// one line, and one per row is a single column — so no separate arrangement is needed.
export function sponsorRows(count, perRow) {
  if (count <= 0) return []
  const per = Math.max(1, perRow ?? SPONSOR_DEFAULT_PER_ROW)
  const rows = []
  for (let left = count; left > 0; left -= per) rows.push(Math.min(per, left))
  return rows
}

// Mirror of Mismatch.Domain/Overlays/SponsorAreaLayout.Place. The area must be given in
// PIXELS — the 4:3 crop means nothing in canvas percentages, where a horizontal and a
// vertical percent are different lengths. Every logo gets the same size.
export function placeSponsorLogos(areaW, areaH, count, {
  perRow = null, hAlign = 'center', vAlign = 'middle', gap = null,
} = {}) {
  const rows = sponsorRows(count, perRow)
  if (!rows.length || areaW <= 0 || areaH <= 0) return []

  const cols = Math.max(...rows)
  const g = (gap ?? SPONSOR_GAP_FRACTION) * areaW / cols
  const logoW = Math.min(
    (areaW - (cols - 1) * g) / cols,
    ((areaH - (rows.length - 1) * g) / rows.length) * SPONSOR_LOGO_ASPECT,
  )
  if (logoW <= 0) return []
  const logoH = logoW / SPONSOR_LOGO_ASPECT

  const blockH = rows.length * logoH + (rows.length - 1) * g
  const y0 = vAlign === 'top' ? 0 : vAlign === 'bottom' ? areaH - blockH : (areaH - blockH) / 2

  const out = []
  rows.forEach((n, ri) => {
    const rowW = n * logoW + (n - 1) * g
    const x0 = hAlign === 'left' ? 0 : hAlign === 'right' ? areaW - rowW : (areaW - rowW) / 2
    for (let ci = 0; ci < n; ci++) {
      out.push({ x: x0 + ci * (logoW + g), y: y0 + ri * (logoH + g), w: logoW, h: logoH })
    }
  })
  return out
}

// Height (same units as areaW) an area needs to hold `count` logos at their crop with no
// slack — the sponsor band's natural aspect.
export function sponsorAreaHeight(areaW, count, { perRow = null, gap = null } = {}) {
  const rows = sponsorRows(count, perRow)
  if (!rows.length || areaW <= 0) return 0
  const cols = Math.max(...rows)
  const g = (gap ?? SPONSOR_GAP_FRACTION) * areaW / cols
  const logoW = (areaW - (cols - 1) * g) / cols
  return rows.length * (logoW / SPONSOR_LOGO_ASPECT) + (rows.length - 1) * g
}

// The sponsor slots as percentages OF THE AREA, ready to position inside the drawn box.
export function sponsorSlotsPct(placement = {}, count = SPONSOR_PREVIEW_COUNT) {
  const areaW = ((placement.imageWidth ?? placement.w ?? 0) / 100) * CANVAS_W
  const areaH = ((placement.imageHeight ?? placement.h ?? 0) / 100) * CANVAS_H
  return placeSponsorLogos(areaW, areaH, count, placement).map((b) => ({
    left: (b.x / areaW) * 100,
    top: (b.y / areaH) * 100,
    width: (b.w / areaW) * 100,
    height: (b.h / areaH) * 100,
  }))
}

const DEFAULT_IMAGE_W = 8
// Five 4:3 logos need real width; a square default box could not hold one row.
const DEFAULT_SPONSOR_AREA_W = 40

// A dropped bar starts at the size the bundled Team Comparison bars use, so it lands
// visible and roughly right on artwork based on our defaults.
export const DEFAULT_BAR_W = 18.85
export const DEFAULT_BAR_H = 1.11

// A dropped color box starts at the size the bundled Team Comparison colour stripes use.
export const DEFAULT_COLOR_BOX_W = 0.63
export const DEFAULT_COLOR_BOX_H = 8.89

// Build a fresh box-model placement for a field that's just been dropped onto
// the canvas at (x, y). Text fields get a text-area box (center/middle/wrap
// defaults); image fields get a draw-area box at their crop's aspect. Neither
// carries the legacy offset/container/anchor fields.
export function newFieldPlacement(field, { x, y }) {
  if (field.dataType === 'Bar') {
    return {
      fieldId: field.key,
      x,
      y,
      w: DEFAULT_BAR_W,
      h: DEFAULT_BAR_H,
      hAlign: field.key.startsWith('home_') ? 'right' : 'left',
    }
  }
  if (field.dataType === 'ColorBox') {
    return { fieldId: field.key, x, y, w: DEFAULT_COLOR_BOX_W, h: DEFAULT_COLOR_BOX_H }
  }
  if (field.dataType === 'Image') {
    if (field.key === SPONSORS_FIELD_ID) {
      const areaWpx = (DEFAULT_SPONSOR_AREA_W / 100) * CANVAS_W
      return {
        fieldId: field.key,
        x,
        y,
        imageWidth: DEFAULT_SPONSOR_AREA_W,
        imageHeight: +((sponsorAreaHeight(areaWpx, SPONSOR_PREVIEW_COUNT) / CANVAS_H) * 100).toFixed(2),
        hAlign: 'center',
        vAlign: 'middle',
      }
    }
    return {
      fieldId: field.key,
      x,
      y,
      imageWidth: DEFAULT_IMAGE_W,
      imageHeight: imageHeightPctFor(DEFAULT_IMAGE_W, imageAspectFor(field.key)),
    }
  }
  return {
    fieldId: field.key,
    x,
    y,
    w: DEFAULT_TEXT_W,
    h: defaultTextHeight(DEFAULT_FONT_SIZE),
    hAlign: 'center',
    vAlign: 'middle',
    fit: 'wrap',
    fontFamily: 'Roboto',
    fontWeight: 400,
    fontSize: DEFAULT_FONT_SIZE,
    color: '#ffffff',
  }
}
